// Shared score input primitive. Used by both the admin result form and the
// user prediction form. Renders as a big +/- stepper pair matching the
// Continental '26 design; a score ranges 0–99.

import { TeamFlag } from '@/components/TeamFlag';

export type Score = { team1: number; team2: number };

interface Side {
  label?: string;
  flagCode?: string | null;
  flagUrl?: string | null;
}

interface ScoreInputProps {
  label: string;
  value: Score;
  onChange: (next: Score) => void;
  disabled?: boolean;
  /** Optional per-team labels / flags shown above each stepper. */
  team1?: Side;
  team2?: Side;
}

function clamp(n: number) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(99, Math.round(n)));
}

function Stepper({
  value,
  onChange,
  disabled,
  side,
}: {
  value: number;
  onChange: (n: number) => void;
  disabled?: boolean;
  side?: Side;
}) {
  return (
    <div className="predict-col">
      {side ? (
        <div className="lbl">
          {side.flagCode ? (
            <TeamFlag
              code={side.flagCode}
              url={side.flagUrl ?? null}
              size="xs"
            />
          ) : null}
          {side.label}
        </div>
      ) : null}
      <div className="predict-big">
        <button
          type="button"
          onClick={() => onChange(clamp(value - 1))}
          disabled={disabled}
          aria-label="decrease"
        >
          −
        </button>
        <div className="v tabular">{value}</div>
        <button
          type="button"
          onClick={() => onChange(clamp(value + 1))}
          disabled={disabled}
          aria-label="increase"
        >
          +
        </button>
      </div>
    </div>
  );
}

export function ScoreInput({
  label,
  value,
  onChange,
  disabled,
  team1,
  team2,
}: ScoreInputProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {label ? (
        <div
          style={{
            fontSize: 10,
            letterSpacing: '0.22em',
            color: 'var(--fg-muted)',
            textTransform: 'uppercase',
            fontWeight: 600,
          }}
        >
          {label}
        </div>
      ) : null}
      <div className="predict-inputs">
        <Stepper
          value={value.team1}
          onChange={(n) => onChange({ ...value, team1: n })}
          disabled={disabled}
          side={team1}
        />
        <div className="predict-mid">–</div>
        <Stepper
          value={value.team2}
          onChange={(n) => onChange({ ...value, team2: n })}
          disabled={disabled}
          side={team2}
        />
      </div>
    </div>
  );
}
